import type { DocumentRef } from '../../lib/documentReader'
import type { ViewScope } from '../navigation/context'
import { recoveryQuery, type RecoveryItem } from './recovery'

export type SourceValue =
  string | number | boolean | null | SourceValue[] | { [key: string]: SourceValue }
export interface ClassQuestion {
  publication_id?: string
  kind?: 'investment_reference' | 'loan_structure'
  reference_kind?: 'seller_position'
  ordinal: number
  instrument_name: string
  holder_name: string | null
  issuer_name: string | null
  classes: readonly string[]
  original_values: Record<string, SourceValue>
  prompt: string
  citation: { document_id: string; page: number; quote: string }
  document: DocumentRef
  source_citation?: ClassQuestion['citation']
  class_context?: { citation: ClassQuestion['citation']; document: DocumentRef }
}
export interface SourceQuestionRead {
  task_id: string
  read_revision: string
  question: { complete: boolean; questions: readonly ClassQuestion[] }
}
export interface SourceRequest {
  request_id: string
  task_id: string
  status: 'draft'
  requested_document: string
  counterparty_name: string
  earlier_review: boolean
  outbound_available: false
  financial_authority: false
}
function invalid(): never {
  throw new Error('The source question could not be verified. Refresh this work.')
}
function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return invalid()
  return value as Record<string, unknown>
}
function string(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) return invalid()
  return value
}
function quoteTypography(value: string): string {
  return value
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/["“] *([^"“”]*?) *["”]/g, '"$1"')
    .replace(/(\p{L})['’](?=\p{L})/gu, "$1'")
}
function identifier(value: unknown): string {
  const text = string(value)
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(text))
    return invalid()
  return text
}
function hash(value: unknown): string {
  const text = string(value)
  if (!/^[a-f0-9]{64}$/.test(text)) return invalid()
  return text
}
function jsonValue(value: unknown, depth = 0): asserts value is SourceValue {
  if (depth > 8) return invalid()
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return
  if (typeof value === 'number' && Number.isFinite(value)) return
  if (Array.isArray(value)) {
    for (const item of value) jsonValue(item, depth + 1)
    return
  }
  if (typeof value === 'object' && value !== null) {
    for (const item of Object.values(value)) jsonValue(item, depth + 1)
    return
  }
  return invalid()
}
export function sourceQuestionPath(scope: ViewScope, taskId: string): string {
  const url = new URL(recoveryQuery(scope, { taskId }), 'https://nolos.invalid')
  return url.pathname + '/source-question' + url.search
}
export function sourceRequestPath(taskId: string): string {
  return '/api/navigation/recovery/' + identifier(taskId) + '/source-question/document-requests'
}
export function parseSourceQuestion(raw: unknown, item: RecoveryItem): SourceQuestionRead {
  const data = object(raw),
    question = object(data.question)
  if (
    data.task_id !== item.task_id ||
    !['multiclass', 'source_questions'].includes(String(item.source_question_kind)) ||
    question.actions_available !== false ||
    question.financial_authority !== false ||
    typeof question.complete !== 'boolean' ||
    !Array.isArray(question.questions)
  )
    return invalid()
  if (!item.documents.some((doc) => doc.document_id === question.document_id)) return invalid()
  hash(question.source_revision)
  hash(question.payload_fingerprint)
  if (
    question.complete
      ? question.questions.length < 1 || question.questions.length > 50
      : question.questions.length !== 0
  )
    return invalid()
  const published = item.source_question_kind === 'source_questions'
  const publications = published ? question.publication_ids : null
  if (
    published &&
    (!Array.isArray(publications) || publications.length !== question.questions.length)
  )
    return invalid()
  const seen = new Set<string>()
  const questions = question.questions.map((raw, index): ClassQuestion => {
    const row = object(raw),
      citation = object(row.citation),
      doc = object(row.document),
      values = object(row.original_values)
    const investment = row.kind === 'investment_reference'
    const seller = investment && row.reference_kind === 'seller_position'
    if (row.reference_kind != null && !seller) return invalid()
    const loan = row.kind === 'loan_structure'
    if (row.kind != null && !investment && !loan) return invalid()
    const publication = published ? identifier((publications as unknown[])[index]) : undefined
    const identity = `${publication ?? 'legacy'}:${row.ordinal}`
    if (seen.has(identity)) return invalid()
    seen.add(identity)
    if (
      (published
        ? !Number.isSafeInteger(row.ordinal) || Number(row.ordinal) < 0
        : row.ordinal !== index) ||
      (investment &&
        (!published || row.proposal_type !== 'create_trade' || row.issuer_name !== null)) ||
      (loan &&
        (!published ||
          row.proposal_type !== 'create_instrument' ||
          row.holder_name !== null ||
          typeof row.issuer_name !== 'string' ||
          !row.issuer_name.trim())) ||
      !(loan ? ['create_instrument'] : ['create_trade', 'create_position']).includes(
        String(row.proposal_type)
      ) ||
      !Array.isArray(row.classes) ||
      (investment || loan ? row.classes.length !== 0 : row.classes.length < 2) ||
      new Set(row.classes).size !== row.classes.length
    )
      return invalid()
    if (
      citation.document_id !== question.document_id ||
      doc.document_id !== question.document_id ||
      !Number.isSafeInteger(citation.page) ||
      Number(citation.page) < 1 ||
      doc.page !== citation.page
    )
      return invalid()
    hash(citation.content_sha256)
    string(citation.revision)
    let sourceCitation: ClassQuestion['citation'] | undefined
    if ('source_citation' in row) {
      const literal = object(row.source_citation)
      if (
        ['document_id', 'content_sha256', 'revision', 'page'].some(
          (key) => literal[key] !== citation[key]
        ) ||
        quoteTypography(string(literal.quote)) !== quoteTypography(string(citation.quote))
      )
        return invalid()
      sourceCitation = {
        document_id: identifier(literal.document_id),
        page: Number(literal.page),
        quote: string(literal.quote),
      }
    }
    if (doc.snippet !== (sourceCitation?.quote ?? citation.quote)) return invalid()
    let classContext: ClassQuestion['class_context']
    if ('class_context' in row) {
      if (investment || loan) return invalid()
      const context = object(row.class_context),
        contextCitation = object(context.citation),
        contextDocument = object(context.document)
      if (
        ['document_id', 'content_sha256', 'revision', 'page'].some(
          (key) => contextCitation[key] !== citation[key]
        ) ||
        contextDocument.document_id !== citation.document_id ||
        contextDocument.page !== citation.page ||
        contextDocument.snippet !== contextCitation.quote ||
        string(contextCitation.quote).trim().split(/\s+/).join(' ') !==
          string(row.instrument_name).trim().split(/\s+/).join(' ')
      )
        return invalid()
      classContext = {
        citation: {
          document_id: identifier(contextCitation.document_id),
          page: Number(contextCitation.page),
          quote: string(contextCitation.quote),
        },
        document: {
          document_id: identifier(contextDocument.document_id),
          display_title:
            contextDocument.display_title == null ? null : string(contextDocument.display_title),
          page: Number(contextDocument.page),
          snippet: string(contextDocument.snippet),
        },
      }
    }
    const expectedValues = loan
      ? [
          'instrument_type',
          'category',
          'commitment_based',
          'currency',
          'total_issued',
          'total_commitments',
          'balance_as_of',
          'balance_date_source',
          'balance_date_role',
          'balance_date_context',
          'nominal_value',
          'nominal_value_source',
          'stated_capital',
          'stated_capital_currency',
          'stated_capital_source',
        ]
      : investment
        ? [
            'effective_date',
            'units_delta',
            'consideration',
            'trade_name',
            'transaction_type',
            'unverified_facts',
            'distribution_subtype',
          ]
        : [
            'units_held',
            'units_delta',
            'consideration_amount',
            'consideration_currency',
            'effective_date',
            'transaction_type',
          ]
    const optionalValues = investment
      ? ['date_facts', 'nominal_amount', 'nominal_amount_source']
      : []
    if (
      expectedValues.some((key) => !(key in values)) ||
      Object.keys(values).some(
        (key) => !expectedValues.includes(key) && !optionalValues.includes(key)
      ) ||
      (investment && 'nominal_amount' in values !== 'nominal_amount_source' in values)
    )
      return invalid()
    if (loan) {
      if (values.category !== 'debt' || values.commitment_based !== null) return invalid()
      for (const value of Object.values(values)) jsonValue(value)
    }
    if (investment) {
      const sellerReference =
        values.transaction_type === 'disposal' ||
        (['share_transfer', 'transfer'].includes(String(values.transaction_type)) &&
          typeof values.units_delta === 'number' &&
          values.units_delta < 0)
      if (
        (seller ? !sellerReference : values.transaction_type !== 'capital_call') ||
        !Array.isArray(values.unverified_facts) ||
        values.unverified_facts.some((value) => typeof value !== 'string')
      )
        return invalid()
      const consideration = object(values.consideration)
      if (!['cash', 'in_kind'].includes(String(consideration.kind))) return invalid()
      for (const value of Object.values(values)) jsonValue(value)
    }
    for (const value of investment || loan ? [] : Object.values(values))
      if (
        value !== null &&
        ((typeof value !== 'string' && typeof value !== 'number') ||
          (typeof value === 'number' && !Number.isFinite(value)))
      )
        return invalid()
    return {
      ordinal: Number(row.ordinal),
      ...(classContext ? { class_context: classContext } : {}),
      ...(sourceCitation ? { source_citation: sourceCitation } : {}),
      ...(publication ? { publication_id: publication } : {}),
      ...(investment ? { kind: 'investment_reference' as const } : {}),
      ...(seller ? { reference_kind: 'seller_position' as const } : {}),
      ...(loan ? { kind: 'loan_structure' as const } : {}),
      instrument_name: string(row.instrument_name),
      holder_name: row.holder_name == null ? null : string(row.holder_name),
      issuer_name: row.issuer_name == null ? null : string(row.issuer_name),
      classes: row.classes.map(string),
      original_values: values as ClassQuestion['original_values'],
      prompt: string(row.prompt),
      citation: {
        document_id: identifier(citation.document_id),
        page: Number(citation.page),
        quote: string(citation.quote),
      },
      document: {
        document_id: identifier(doc.document_id),
        display_title: doc.display_title == null ? null : string(doc.display_title),
        page: Number(doc.page),
        snippet: string(doc.snippet),
      },
    }
  })
  return {
    task_id: identifier(data.task_id),
    read_revision: hash(data.read_revision),
    question: { complete: question.complete, questions },
  }
}
export function parseSourceRequests(
  raw: unknown,
  taskId: string
): { complete: boolean; requests: SourceRequest[] } {
  const data = object(raw)
  if (
    typeof data.complete !== 'boolean' ||
    !Array.isArray(data.requests) ||
    data.requests.length > 50 ||
    (!data.complete && data.requests.length !== 0)
  )
    return invalid()
  const requests = data.requests.map((raw) => {
    const row = object(raw)
    if (
      row.task_id !== taskId ||
      row.status !== 'draft' ||
      row.outbound_available !== false ||
      row.financial_authority !== false ||
      typeof row.earlier_review !== 'boolean'
    )
      return invalid()
    return {
      request_id: identifier(row.request_id),
      task_id: identifier(row.task_id),
      status: 'draft' as const,
      requested_document: string(row.requested_document),
      counterparty_name: string(row.counterparty_name),
      earlier_review: row.earlier_review,
      outbound_available: false as const,
      financial_authority: false as const,
    }
  })
  if (new Set(requests.map((row) => row.request_id)).size !== requests.length) return invalid()
  return { complete: data.complete, requests }
}
